import 'react-toggle/style.css';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import Timeago from 'react-timeago';

import { DateTime } from 'luxon';
import { HEARTBEAT_WARNING_LIMIT } from 'src/config';
import heartbeatBadIcon from 'src/images/customIcons/heartbeat-bad.png';
import heartbeatGoodIcon from 'src/images/customIcons/heartbeat-good.png';
import IDisk from 'src/ServerEntities/IDisk';
import IRhapsodyMetric from 'src/ServerEntities/IRhapsodyMetric';
import IRhapsodyServerPanel from 'src/ServerEntities/IRhapsodyServerPanel';
import { formatTime } from 'src/UsefulFunctions/dateConverter';

interface IProps {
    data: IRhapsodyServerPanel;
    token: string;
    interystems?: boolean;
    showName?: boolean;
};

const ContentContainer = styled.main`
    display: flex;
    flex-direction: column;
    margin-right: 15px;
    height: 100%;
`;

const OuterContainer = styled.div`
    width: 350px;
    border-radius: 25px;
    border: 5px solid #44A2D6;
    padding: 2px;
    display: flex;
    flex-direction: column;
    margin-right: 15px;
    margin-bottom: 15px;
`;

const InnerContainer = styled.div`
    padding: 2% 10px;
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const TitleBar = styled.div`
    width: 340px;
    border-radius: 20px;
    background-color: #1577AE;
    padding: 5px;
    color: #FFF;
    text-align: center;
    font-size: 1.2em;
    font-weight: bold;
    cursor: pointer;
    flex-shrink: 0;
`;

const ComponentContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1 0 auto;
`;

const ComponentHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0 12px;
    font-weight: bold;
    font-size: 0.9em; 
    margin-bottom: 1%;
`;

const ComponentBar = styled.div`
    display: flex;
    width: 100%; 
    height: 40px;
    min-height: 30px; 
    border: 0px solid black;
    border-radius: 20px;
    overflow: hidden;
    max-height: 40px;
    margin-bottom: 8px;
`;

const ComponentName = styled.div`

`;

const ComponentAmount = styled.div`

`;

const HearbeatBar = styled.div`
    width: 100%;
    height: 100%;
    min-height: 30px;
    border-radius: 20px;
    text-align: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-left: 10px;
    padding-right: 10px;
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
    max-height: 40px;
`;

const UptimeBar = styled.div`
    height: 100%;
    min-height: 30px;
    border-radius: 30px;
    max-height: 40px;
    text-align: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding-left: 10px;
    padding-right: 10px;
    border: 3px solid #1577AE;
    margin-bottom: 10px;
`;

const renderBar = (name: string, percentage: number) => {
    const redWidth = percentage;
    const blueWidth = 100 - percentage;

    return (
        <ComponentContainer>
            <ComponentHeader>
                <ComponentName>{name}</ComponentName>
                <ComponentAmount>{percentage ? percentage.toLocaleString("en-US", { maximumFractionDigits: 2}) : ""}%</ComponentAmount>
            </ComponentHeader>
            <ComponentBar>
                <div style={{ 
                    width: `${redWidth}%`, 
                    backgroundColor: 'red', 
                    borderTopLeftRadius: '15px', 
                    borderBottomLeftRadius: '15px',
                    height: '100%'
                }}></div>
                <div style={{ 
                    width: `${blueWidth}%`, 
                    backgroundColor: '#1577AE',
                    height: '100%'
                }}></div>
            </ComponentBar>
        </ComponentContainer>
    );
};

export { renderBar, ContentContainer, InnerContainer, OuterContainer, TitleBar }

const ServerDetailsPanel = (props: IProps) => {
    const { data } = props;
    
    // Validate lastHeartbeat before using it
    const isValidHeartbeat = data?.lastHeartbeat && !isNaN(data.lastHeartbeat) && data.lastHeartbeat > 0;
    const heartbeatHealthy = isValidHeartbeat && data.lastHeartbeat > (DateTime.now().toMillis() - HEARTBEAT_WARNING_LIMIT);
    
    const navigate = useNavigate();

    const handleClick = () => {
      navigate('/Ram/Servers');
    };

    const calculateMemoryUsed = (memory: IRhapsodyMetric) => {
        if (!memory) {
            return 0;
        }
        if (memory.percent) {
            return memory.percent;
        }
        return Math.floor(((memory.inUse + memory.jvmInUse + memory.working) / memory.totalAllocated) * 100);
    };

    const calculateWSThreadsUsed = (wsThreads: IRhapsodyMetric) => {
        if(!wsThreads){
            return 0;
        }
        return Math.floor((wsThreads.currentActiveThreadCount / wsThreads.maxTotalConnections) * 100);
    };

    const renderDiskSpaceMetrics = (metrics: IDisk) => {
        let percentFree: number = 0;
        
        if (metrics.percentFree) {
            if (typeof metrics.percentFree === 'string') {
                percentFree = parseFloat(metrics.percentFree.replace("%", ""));
            } else if (typeof metrics.percentFree === 'number') {
                percentFree = metrics.percentFree;
            }
        }
        
        return renderBar("Disk usage - " + metrics.diskId, 100 - percentFree);
    }

    const serverMemory = data.memory && data.memory.serverPercent
        ? data.memory.serverPercent
        : calculateMemoryUsed(data.memory);

    return (
        <OuterContainer>
            <TitleBar onClick={handleClick}>{props.showName ? data.serverName : "Server Details"}</TitleBar>
            <InnerContainer>
                <ComponentHeader>
                    <ComponentName>Last heartbeat</ComponentName>
                </ComponentHeader>
                <ComponentBar>
                    <HearbeatBar style={{ backgroundColor: heartbeatHealthy ? '#5CEA5A' : '#FF0000' }}>
                        <div style={{ width: "32px" }}></div>
                        {isValidHeartbeat ? (
                            <Timeago key={`last-heartbeat-timeago-${props.token}-${data.lastHeartbeat}`} date={data.lastHeartbeat} />
                        ) : (
                            <span>No heartbeat data</span>
                        )}
                        <img height={28} width={28} src={heartbeatHealthy ? heartbeatGoodIcon : heartbeatBadIcon} />
                    </HearbeatBar>
                </ComponentBar>
                <ComponentHeader>
                    <ComponentName>TIE Uptime</ComponentName>
                </ComponentHeader>
                <UptimeBar>
                    {data.tieLastBoot ? formatTime((Date.now() - data.tieLastBoot) / 1000) : "Not known"}
                </UptimeBar>
                <ComponentHeader>
                    <ComponentName>Server Uptime</ComponentName>
                </ComponentHeader>
                <UptimeBar>
                    {data.lastBoot ? formatTime((Date.now() - data.lastBoot) / 1000) : "Not known"}
                </UptimeBar>
                {!props.interystems && <div>
                    {data.spaceMetrics.map((diskMetric) => (
                        <div key={diskMetric.computerName}>
                            {renderDiskSpaceMetrics(diskMetric)}
                        </div>
                    ))}
                </div>}
                {renderBar("CPU", data.cpu ? data.cpu.percent : 0)}
                {!!serverMemory && renderBar("Server memory", serverMemory)}
                {(data.memory && data.memory.tiePercent != null) ? renderBar("TIE memory", data.memory.tiePercent) : null}
                {!props.interystems && data.wsThreads && renderBar("WS threads usage", calculateWSThreadsUsed(data.wsThreads))}
                {props.interystems && renderBar("Paging activity", data.pagingActivity ? data.pagingActivity.percent : 0)}
            </InnerContainer>
        </OuterContainer>
    );
};


export default ServerDetailsPanel;